import {useCallback} from 'react';
import {List} from 'immutable';

import {addGenericErrorToast} from '@albert-io/atomic';
import {resource} from '@albert-io/json-api-framework/request/builder';
import type {ClassroomModelV1} from '@albert-io/models';
import masqueradeStore from 'client/generic/Masquerade/Masquerade.store';

import {useTeacherDashboardStore} from '../useTeacherDashboardStore';

export const getTeacherSidemenuClassroomsQuery = (status: 'active' | 'archived', nameSearch = '') =>
  resource('classrooms_v1')
    .mandarkEndpoint(['teachers_v1', masqueradeStore.getUserIdByMasqueradeState(), 'classrooms_v1'])
    .include('subjects_v2.curriculum_area_v1')
    .filter({status})
    .filter({school_v5: masqueradeStore.getSchoolId()})
    // Second arg gates the filter so an empty term returns the full list.
    .filter({name: {case_insensitive_substring: nameSearch}}, !!nameSearch)
    .sort('name')
    .pageSize(100);

/**
 * Fetch actions only — subscribes to the zustand setters (stable refs) but NOT the classroom lists,
 * so action-only callers (create-class, Clever sync, settings mutations) don't re-render when a list
 * is replaced. Fetchers are `useCallback`-stable so they're safe in effect dep arrays.
 */
export const useTeacherClassroomActions = () => {
  const setArchivedClassrooms = useTeacherDashboardStore((state) => state.setArchivedClassrooms);
  const setAreArchivedClassroomsLoading = useTeacherDashboardStore(
    (state) => state.setAreArchivedClassroomsLoading
  );
  const setAreClassroomsLoading = useTeacherDashboardStore(
    (state) => state.setAreClassroomsLoading
  );
  const setClassrooms = useTeacherDashboardStore((state) => state.setClassrooms);

  const fetchWith = useCallback(
    (
      status: 'active' | 'archived',
      setLoading: (isLoading: boolean) => void,
      setResults: (results: List<ClassroomModelV1>) => void
    ) => {
      setLoading(true);

      // Built per fetch so masquerade identity switches are picked up.
      const query = getTeacherSidemenuClassroomsQuery(status);
      query.invalidateInterest();

      query
        .getResourcePromise()
        .then((fetchedClassrooms) => {
          // The legacy request builder is untyped; this endpoint returns classrooms.
          setResults(fetchedClassrooms as List<ClassroomModelV1>);
        })
        .catch(() => {
          setResults(List([]));
          addGenericErrorToast();
        })
        .finally(() => {
          setLoading(false);
        });
    },
    []
  );

  const fetchClassrooms = useCallback(() => {
    fetchWith('active', setAreClassroomsLoading, setClassrooms);
  }, [fetchWith, setAreClassroomsLoading, setClassrooms]);

  const fetchArchivedClassrooms = useCallback(() => {
    fetchWith('archived', setAreArchivedClassroomsLoading, setArchivedClassrooms);
  }, [fetchWith, setAreArchivedClassroomsLoading, setArchivedClassrooms]);

  return {fetchArchivedClassrooms, fetchClassrooms};
};

export const useTeacherClassrooms = () => {
  const archivedClassrooms = useTeacherDashboardStore((state) => state.archivedClassrooms);
  const areArchivedClassroomsLoading = useTeacherDashboardStore(
    (state) => state.areArchivedClassroomsLoading
  );
  const areClassroomsLoading = useTeacherDashboardStore((state) => state.areClassroomsLoading);
  const classrooms = useTeacherDashboardStore((state) => state.classrooms);
  const {fetchArchivedClassrooms, fetchClassrooms} = useTeacherClassroomActions();

  return {
    archivedClassrooms,
    areArchivedClassroomsLoading,
    areClassroomsLoading,
    classrooms,
    fetchArchivedClassrooms,
    fetchClassrooms
  };
};
